<template>
  <div class="relative min-h-[400px]">
    <site-loader v-if="loading" :top="true" />
    <div v-show="step == 1">
      <div
        class="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-5 mb-3.5 md:mb-5"
      >
        <div class="flex mb-5">
          <div
            v-for="(label, value) in genders"
            :key="value"
            :class="
              'me-3.5 px-5 py-1.5 rounded-md font-medium block border cursor-pointer text-center text-sm' +
              (errors.includes('gender')
                ? ' border-red-500'
                : person.gender == value
                  ? ' bg-white border-red-500'
                  : ' bg-dark-20 border-white')
            "
            @click="updatePerson('gender', value)"
          >
            <span class="tiny-screen-hidden">{{ label }}</span>
            <span class="tiny-screen-visible">{{ label.charAt(0) }}</span>
          </div>
        </div>
        <form-select
          :label="t('Postcode / City')"
          :options="postcodes"
          :error="errors.includes('postcode')"
          :value="person.postcode?.value || 0"
          :initial-value="postcodeLoaded ? undefined : initialPostcode"
          :has-search="true"
          :no-matches-label="t('No postcodes found')"
          @change="updatePerson('postcode', $event)"
          @search="searchPostcode"
        />
      </div>
      <div v-if="additionalsLoaded">
        <div class="flex relative items-center mb-5">
          <label class="text-dark-1 font-medium block">{{
            t("Products")
          }}</label>
          <tool-tip v-if="type" :html="type.tooltip" />
        </div>
        <div v-if="!type" class="text-sm mb-5">
          {{ t("No products found for your criterias") }}
        </div>
        <div
          v-if="type"
          class="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-5 mb-5"
        >
          <calculator-additional
            v-for="(additional, index) in type.additionals"
            :key="index"
            :additional="additional"
            :selected="
              person.additionals.map((a) => a.id).includes(additional.id)
            "
            @change="toggleAdditional(additional, $event.target.checked)"
          />
        </div>
        <div v-if="Object.keys(otherTypes).length">
          <label class="text-dark-1 font-medium mb-5 block">{{
            t("Additional Products")
          }}</label>
          <div
            v-for="otherTypeId in Object.keys(otherTypes)"
            :key="otherTypeId"
            class="mb-5"
          >
            <div class="flex relative items-center mb-5">
              <label class="text-dark-1 text-xs font-medium block title">{{
                otherTypes[otherTypeId].title
              }}</label>
              <tool-tip :html="otherTypes[otherTypeId].tooltip" />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-5">
              <calculator-additional
                v-for="(additional, index) in otherTypes[otherTypeId]
                  .additionals"
                :key="index"
                :additional="additional"
                :selected="
                  person.extraAdditionals
                    .map((a) => a.id)
                    .includes(additional.id)
                "
                @change="
                  toggleExtraAdditional(additional, $event.target.checked)
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-show="step == 2">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-5 mb-5">
        <form-input
          :label="t('First Name')"
          :value="person.firstName"
          type="text"
          :error="errors.includes('firstName')"
          @change="updatePerson('firstName', $event.target.value)"
        />
        <form-input
          :label="t('Last Name')"
          :value="person.lastName"
          type="text"
          :error="errors.includes('lastName')"
          @change="updatePerson('lastName', $event.target.value)"
        />
        <form-input
          :label="t('Email')"
          :value="person.email"
          type="email"
          :error="errors.includes('email')"
          @change="updatePerson('email', $event.target.value)"
        />
      </div>
    </div>
  </div>
</template>

<script>
/* globals Craft $ */
import { debounce } from "lodash";
import FormInput from "./forms/FormInput.vue";
import FormSelect from "./forms/FormSelect.vue";
import CalculatorAdditional from "./CalculatorAdditional.vue";
import ToolTip from "./ToolTip.vue";
import SiteLoader from "./SiteLoader.vue";

export default {
  components: {
    ToolTip,
    FormInput,
    FormSelect,
    CalculatorAdditional,
    SiteLoader,
  },
  props: {
    person: {
      type: Object,
      required: true,
    },
    step: {
      type: Number,
      required: true,
    },
    initialPostcode: {
      type: Object,
    },
    errors: {
      type: Array,
    },
    typeId: {
      type: Number,
      required: true,
    },
    otherTypeIds: {
      type: Array,
    },
  },
  emits: ["update"],
  data() {
    return {
      postcodes: [],
      type: null,
      otherTypes: [],
      loading: false,
      genders: {
        male: this.t("Male"),
        female: this.t("Female"),
        prenatal: this.t("Prenatal"),
      },
      additionalsLoaded: false,
      postcodeLoaded: false,
      debouncePostcodes: debounce((event) => {
        this.loadPostcodes(event.target.value);
      }, 500),
    };
  },
  watch: {
    person: {
      handler(newVal, oldVal) {
        this.onPersonChange(newVal, oldVal);
      },
      deep: true,
    },
    postcodes() {
      this.$nextTick(() => {
        $(this.$refs.postcodeInput).multipleSelect("refresh");
      });
    },
  },
  created() {
    this.onPersonChange(this.person, {});
  },
  methods: {
    onPersonChange(newVal, oldVal) {
      if (
        newVal.postcode?.value !== oldVal.postcode?.value ||
        newVal.gender !== oldVal.gender
      ) {
        this.loadAdditionals();
      }
    },
    updatePerson(field, value) {
      this.$emit("update", {
        [field]: value,
      });
    },
    searchPostcode(event) {
      return this.debouncePostcodes(event);
    },
    loadPostcodes(term) {
      if (term.length < 3) {
        return;
      }
      this.loading = true;
      $.ajax({
        url: Craft.currentSite.baseUrl,
        method: "post",
        data: {
          action: "swissmed/calculator/postcodes",
          countryCode: Craft.currentSite.countryCode,
          term: term,
        },
      }).done((data) => {
        this.postcodes = data;
        this.postcodeLoaded = true;
        this.loading = false;
      });
    },
    loadAdditionals() {
      if (!this.person.postcode?.value || !this.person.gender) {
        return;
      }
      this.loading = true;
      $.ajax({
        url: Craft.currentSite.baseUrl,
        method: "post",
        data: {
          action: "swissmed/calculator/additionals",
          optionals: 0,
          country: Craft.currentSite.countryCode,
          postcode: this.person.postcode?.value,
          gender: this.person.gender,
        },
      }).done((data) => {
        this.type = data[this.typeId] || null;
        let otherTypes = [];
        for (let id of this.otherTypeIds || []) {
          if (data[id]) {
            otherTypes.push(data[id]);
          }
        }
        this.otherTypes = otherTypes;
        this.additionalsLoaded = true;
        this.updatePerson("additionals", []);
        this.updatePerson("extraAdditionals", []);
        this.loading = false;
      });
    },
    toggleExtraAdditional(additional, enabled) {
      this.updatePerson(
        "extraAdditionals",
        this._toggleAdditional(
          additional,
          enabled,
          this.person.extraAdditionals,
        ),
      );
    },
    toggleAdditional(additional, enabled) {
      this.updatePerson(
        "additionals",
        this._toggleAdditional(additional, enabled, this.person.additionals),
      );
    },
    _toggleAdditional(additional, enabled, list) {
      let adds = [];
      if (enabled) {
        for (let add of list) {
          if (add.group_id != additional.group_id) {
            adds.push(add);
          }
        }
        adds.push(additional);
        return adds;
      }
      return list.filter((a) => a.id != additional.id);
    },
  },
};
</script>
